import { Button } from "@/components/ui/button";
import { Search, User, Heart, Menu, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LocaleLink, LocaleNavLink } from "@/components/LocaleLink";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import LanguageSelector from "@/components/LanguageSelector";
import { useState } from "react";
import logo from "@/assets/logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";

const Header = () => {
  const { user, signOut, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useLocaleNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navLinks = [
    { to: "/properties", label: t.nav.deals },
    { to: "/pricing", label: t.nav.pricing },
    { to: "/for-agents", label: t.nav.forAgents },
    { to: "/blog", label: t.nav.blog },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <LocaleLink to="/" className="flex items-center gap-2">
          <img src={logo} alt="Dealigator" className="h-8 w-8" />
          <span className="text-xl font-bold text-primary">Dealigator</span>
        </LocaleLink>
        
        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <LocaleNavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? 'font-bold text-primary' : 'font-medium text-muted-foreground hover:text-foreground'}`
              }
            >
              {link.label}
            </LocaleNavLink>
          ))}
        </nav>
        
        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSelector />
          
          {/* Desktop auth buttons */}
          <div className="hidden md:flex items-center gap-2">
            {loading ? (
              <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {user.email?.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=filters')}>
                    {t.nav.myFilters}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=alerts')}>
                    {t.nav.myAlerts}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/favorites')}>
                    <Heart className="h-4 w-4 mr-2" />
                    {t.nav.favorites || 'Любими'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    {t.nav.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
                {t.nav.signIn}
              </Button>
            )}
            {!user && (
              <Button size="sm" onClick={() => navigate('/auth')} className="gap-1.5">
                {t.nav.startFree}
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Mobile slide-out menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[280px] p-0">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2">
              <img src={logo} alt="Dealigator" className="h-7 w-7" />
              <span className="text-lg font-bold text-primary">Dealigator</span>
            </SheetTitle>
          </SheetHeader>

          <nav className="flex flex-col p-4 gap-1">
            {navLinks.map((link) => (
              <SheetClose asChild key={link.to}>
                <LocaleNavLink
                  to={link.to}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'font-bold text-primary bg-primary/5'
                        : 'font-medium text-foreground hover:bg-muted'
                    }`
                  }
                >
                  {link.label}
                </LocaleNavLink>
              </SheetClose>
            ))}
          </nav>

          {!user && (
            <div className="border-t border-border p-4 space-y-2">
              <SheetClose asChild>
                <Button className="w-full gap-1.5" onClick={() => navigate('/auth')}>
                  {t.nav.startFree}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </SheetClose>
            </div>
          )}

          <div className="border-t border-border p-4 space-y-2">
            {loading ? (
              <div className="h-9 bg-muted animate-pulse rounded-md" />
            ) : user ? (
              <>
                <SheetClose asChild>
                  <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => navigate('/favorites')}>
                    <Heart className="h-4 w-4" />
                    {t.nav.favorites || 'Любими'}
                  </Button>
                </SheetClose>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive"
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                >
                  {t.nav.signOut}
                </Button>
              </>
            ) : (
              <SheetClose asChild>
                <Button variant="ghost" className="w-full" onClick={() => navigate('/auth')}>
                  {t.nav.signIn}
                </Button>
              </SheetClose>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
